import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import ChatInterface from './components/ChatInterface';
import FileManager from './components/FileManager';
import FeedbackForm from './components/FeedbackForm';
import Auth from './components/Auth';
import { APP_VERSION } from './version';

function App() {
  const [user, loading] = useAuthState(auth);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'files', 'feedback'
  const [language, setLanguage] = useState('en'); // 'en' or 'zh-TW'

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Fetch runtime configuration on app startup
  useEffect(() => {
    fetch('/api/config')
      .then(res => res.json())
      .then(data => {
        setConfig({
          ...data,
          clearChatWebhook: data.clearChatWebhook || 'https://n8n-dev.astraventure.ai/webhook/de8f0bc8-c360-4759-a8e5-49be6873d828'
        });
        console.log('Runtime config loaded:', data);
      })
      .catch(err => {
        console.error('Failed to load config:', err);
        // Fallback to defaults
        setConfig({
          knowledgeMode: false,
          trainingMode: false,
          chatTitle: 'AI Agent',
          chatDescription: 'Ask me anything! I\'m here to help with your questions.',
          chatWelcomeTitle: 'Start a Conversation',
          clearChatWebhook: 'https://n8n-dev.astraventure.ai/webhook/de8f0bc8-c360-4759-a8e5-49be6873d828'
        });
      });
  }, []);

  // Show loading while authenticating
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Show auth screen if not logged in
  if (!user) {
    return <Auth />;
  }

  // Show loading while config is being fetched
  if (!config) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const knowledgeMode = config.knowledgeMode;
  const webAssistantMode = config.webAssistantMode;
  const mode = webAssistantMode ? 'web-assistant' : knowledgeMode ? 'knowledge' : 'basic';

  const getPageTitle = () => {
    if (activeTab === 'chat') return 'Chat Agent';
    if (activeTab === 'files') return 'Knowledge Base';
    if (activeTab === 'feedback') return 'Get Started';
    if (activeTab === 'profile') return 'Profile';
    return 'AstraVenture';
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Header - Hidden on desktop */}
      <header className="lg:hidden fixed top-0 z-30 w-full bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="flex items-center justify-between px-4 h-14">
          <img 
            src="/av_icon_v1_gradient_padding.png" 
            alt="AstraVenture Logo" 
            className="w-8 h-8 object-contain"
          />
          <h1 className="text-base font-bold tracking-tight text-gray-900">
            {getPageTitle()}
          </h1>
          {activeTab === 'chat' && (
            <div className="inline-flex rounded-full border border-primary overflow-hidden">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-xs font-semibold transition-all ${
                  language === 'en'
                    ? 'bg-primary text-white'
                    : 'bg-white text-primary'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('zh-TW')}
                className={`px-3 py-1 text-xs font-semibold transition-all ${
                  language === 'zh-TW'
                    ? 'bg-primary text-white'
                    : 'bg-white text-primary'
                }`}
              >
                繁中
              </button>
            </div>
          )}
          {activeTab !== 'chat' && <div className="w-8 h-8"></div>}
        </div>
      </header>

      {/* Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex w-72 bg-primary flex-col shrink-0">
        <div className="p-6 flex items-center gap-3">
          <img 
            src="/av_icon_v1_gradient_padding.png" 
            alt="AstraVenture Logo" 
            className="w-12 h-12 object-contain"
          />
          <span className="text-white font-bold text-xl tracking-tight">AstraVenture</span>
        </div>

        <nav className="flex-1 px-6 space-y-2">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-4 px-4 py-3.5 w-full text-left rounded-input transition-all duration-200 group ${
              activeTab === 'chat'
                ? 'bg-white/10 text-white shadow-md ring-1 ring-white/20'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span className="material-symbols-outlined group-hover:scale-110 transition-transform">chat</span>
            <span className="font-medium text-[16px]">Chat Agent</span>
          </button>

          {knowledgeMode && (
            <button
              onClick={() => setActiveTab('files')}
              className={`flex items-center gap-4 px-4 py-3.5 w-full text-left rounded-input transition-all duration-200 group ${
                activeTab === 'files'
                  ? 'bg-white/10 text-white shadow-md ring-1 ring-white/20'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className="material-symbols-outlined group-hover:scale-110 transition-transform">folder_open</span>
              <span className="font-medium text-[16px]">Knowledge Base</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex items-center gap-4 px-4 py-3.5 w-full text-left rounded-input transition-all duration-200 group ${
              activeTab === 'feedback'
                ? 'bg-white/10 text-white shadow-md ring-1 ring-white/20'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span className="material-symbols-outlined group-hover:scale-110 transition-transform">rocket_launch</span>
            <span className="font-medium text-[16px]">Get Started</span>
          </button>
        </nav>

        {/* User Profile at bottom */}
        <div className="p-6 mt-auto space-y-3">
          <div className="flex items-center gap-3 p-4 bg-white/10 rounded-input border border-white/10 shadow-sm backdrop-blur-sm">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-primary-dark flex items-center justify-center text-sm font-bold text-white ring-2 ring-white/30">
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
              </div>
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-[#7BA8D8] rounded-full"></div>
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-white text-sm font-semibold truncate">
                {user.displayName || 'User'}
              </span>
              <span className="text-white/70 text-xs truncate">{user.email}</span>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-input transition-all border border-white/10"
          >
            <span className="material-symbols-outlined text-[20px]">logout</span>
            <span className="font-medium text-sm">Sign Out</span>
          </button>
          
          <div className="text-center flex flex-col gap-1">
             <span className="text-white/30 text-[10px] uppercase tracking-wider font-medium">v{APP_VERSION}</span>
             <span className="text-white/30 text-[10px] font-medium">© {new Date().getFullYear()} Astra Venture. All rights reserved</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden overflow-x-hidden w-full pt-14 lg:pt-0">
        <div className="flex-1 flex flex-col min-h-0 overflow-x-hidden pb-20 lg:pb-0 w-full">
          {activeTab === 'chat' && <ChatInterface mode={mode} config={config} language={language} setLanguage={setLanguage} user={user} />}
          {activeTab === 'files' && knowledgeMode && <div className="flex-1 overflow-auto min-h-0"><FileManager config={config} user={user} /></div>}
          {activeTab === 'feedback' && <div className="flex-1 overflow-auto min-h-0"><FeedbackForm user={user} /></div>}
          {activeTab === 'profile' && (
            <div className="flex-1 overflow-auto min-h-0 flex flex-col items-center p-6 pt-10 bg-gray-50">
              <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-3xl font-bold text-white shadow-lg shadow-blue-200">
                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-400 border-4 border-white rounded-full"></div>
                </div>
                
                <div className="text-center space-y-1">
                  <h2 className="text-2xl font-bold text-gray-900">{user.displayName || 'User'}</h2>
                  <p className="text-gray-500 font-medium">{user.email}</p>
                </div>

                <div className="w-full pt-4 border-t border-gray-100">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-all font-bold group"
                  >
                    <span className="material-symbols-outlined group-hover:scale-110 transition-transform">logout</span>
                    Sign Out
                  </button>
                </div>
              </div>
              
              <div className="mt-auto mb-8 text-center space-y-2">
                <img 
                  src="/av_icon_v1_gradient_padding.png" 
                  alt="AstraVenture Logo" 
                  className="w-12 h-12 object-contain mx-auto opacity-50 grayscale"
                />
                <div className="text-gray-400 text-sm font-medium flex flex-col items-center">
                  <p>AstraVenture Chatbot</p>
                  <p className="opacity-70">v{APP_VERSION}</p>
                  <p className="text-xs opacity-50 mt-1">© {new Date().getFullYear()} Astra Venture. All rights reserved</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation - Hidden on desktop */}
      <nav className="lg:hidden fixed bottom-0 z-40 w-full bg-white/95 backdrop-blur-xl border-t border-gray-200">
        <div className="flex items-center justify-around h-[60px] max-w-lg mx-auto px-2">
          <button
            onClick={() => setActiveTab('chat')}
            className={`group flex flex-col items-center justify-center w-full h-full gap-1 transition-colors ${
              activeTab === 'chat'
                ? 'text-primary'
                : 'text-gray-400 hover:text-primary'
            }`}
          >
            {activeTab === 'chat' && (
              <div className="absolute top-0 w-12 h-1 bg-primary rounded-b-full shadow-[0_2px_8px_rgba(92,142,183,0.5)]"></div>
            )}
            <span className={`material-symbols-outlined text-[24px] group-hover:-translate-y-0.5 transition-transform duration-200 ${
              activeTab === 'chat' ? 'filled' : ''
            }`}>chat_bubble</span>
            <span className={`text-[10px] ${activeTab === 'chat' ? 'font-bold' : 'font-medium'}`}>Chat</span>
          </button>

          {knowledgeMode && (
            <button
              onClick={() => setActiveTab('files')}
              className={`group flex flex-col items-center justify-center w-full h-full gap-1 transition-colors ${
                activeTab === 'files'
                  ? 'text-primary'
                  : 'text-gray-400 hover:text-primary'
              }`}
            >
              {activeTab === 'files' && (
                <div className="absolute top-0 w-12 h-1 bg-primary rounded-b-full shadow-[0_2px_8px_rgba(92,142,183,0.5)]"></div>
              )}
              <span className={`material-symbols-outlined text-[24px] group-hover:-translate-y-0.5 transition-transform duration-200 ${
                activeTab === 'files' ? 'filled' : ''
              }`}>folder_open</span>
              <span className={`text-[10px] ${activeTab === 'files' ? 'font-bold' : 'font-medium'}`}>Knowledge</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('feedback')}
            className={`group flex flex-col items-center justify-center w-full h-full gap-1 transition-colors ${
              activeTab === 'feedback'
                ? 'text-primary'
                : 'text-gray-400 hover:text-primary'
            }`}
          >
            {activeTab === 'feedback' && (
              <div className="absolute top-0 w-12 h-1 bg-primary rounded-b-full shadow-[0_2px_8px_rgba(92,142,183,0.5)]"></div>
            )}
            <span className={`material-symbols-outlined text-[24px] group-hover:-translate-y-0.5 transition-transform duration-200 ${
              activeTab === 'feedback' ? 'filled' : ''
            }`}>rocket_launch</span>
            <span className={`text-[10px] ${activeTab === 'feedback' ? 'font-bold' : 'font-medium'}`}>Get Started</span>
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`group flex flex-col items-center justify-center w-full h-full gap-1 transition-colors ${
              activeTab === 'profile'
                ? 'text-primary'
                : 'text-gray-400 hover:text-primary'
            }`}
          >
            {activeTab === 'profile' && (
              <div className="absolute top-0 w-12 h-1 bg-primary rounded-b-full shadow-[0_2px_8px_rgba(92,142,183,0.5)]"></div>
            )}
            <span className={`material-symbols-outlined text-[24px] group-hover:-translate-y-0.5 transition-transform duration-200 ${
              activeTab === 'profile' ? 'filled' : ''
            }`}>person</span>
            <span className={`text-[10px] ${activeTab === 'profile' ? 'font-bold' : 'font-medium'}`}>Profile</span>
          </button>
        </div>
      </nav>
    </div>
  );
}

export default App;
